/**
 * This helper is intended to resolve the "cause" argument in a type-safe way when it's not known if it's an Error
 *
 * This is usually the case in a catch block, because the error is usually an Error, but in degenerate situations
 * might be something else. Please only throw Error instances, they're more useful!
 *
 * We type check and return the arguments suitable for use in a call to Error's constructor. This is useful for calling
 * super() from within an error subclass.
 *
 * Use it like this:
 *  class SomeError extends Error {
 *     constructor(cause: unknown) {
 *         super(
 *             ...concatErrorArgs(
 *                 `Some error message`,
 *                 cause,
 *             ),
 *         );
 *
 *         // [...rest of constructor...]
 *     }
 *
 * And it will take care of the `unknown` cause parameter being sensibly logged with the message, via either concatenation
 * or the {cause} option, depending on which is appropriate
 */
export function concatErrorArgs(message, cause) {
    if (!cause) {
        return [message, {}];
    }
    if (cause instanceof Error) {
        return [message, { cause }];
    }
    return [`${message} caused by ${String(cause)}`, {}];
}
/**
 * Helper function for converting an unknown (e.g. from a catch clause) to an Error, preserving as much information as possible
 *
 * @param possibleError An unknown type Error or any other value (e.g. what you caught)
 * @param fallbackMessage A message used when the possibleError wasn't an Error
 */
export function toError(possibleError, fallbackMessage = "Encountered unexpected non-Error error") {
    if (possibleError instanceof Error) {
        return possibleError;
    }
    return new Error(...concatErrorArgs(fallbackMessage, possibleError));
}
//# sourceMappingURL=errors.js.map